import type { SalesViewSummary, SalesFilterOptions, SalesTargetList } from '../../api/types';
import { toggleValue, type SalesFilterState } from './salesViewFilters';
import { TARGET_COLOR, TIER_COLOR, TIER_ORDER } from './salesViewTokens';
import { BAND_ORDER, BAND_LABEL, BAND_TIP } from './salesBandVocab';
import { OverflowRow } from '../shared/OverflowRow';
import { SalesFilterDropdown } from './SalesFilterDropdown';

/** CSE-15d: the tier is health only. Opportunity is weighed in Plan My Week, not here. */
const TIER_HELP =
  'Tier is health only: is this client\'s business with us declining? Opportunity '
  + '(win their business, grow their share, momentum) never changes it; Plan My Week weighs '
  + 'health and opportunity together.';

const BAND_COLOR: Record<string, string> = {
  follow_up_due: '#7B3FA0',
  health_slipping: '#C33A00',
  due_now: '#C0650C',
  coming_up: '#0F7B8A',
  never_contacted: '#1D3E74',
  cadence_slipped: '#6B7280',
  dormant: '#9CA3AF',
  in_rhythm: '#10B981',
  resting: '#A1A1AA',
};

interface SalesChipFiltersProps {
  filters: SalesFilterState;
  summary: SalesViewSummary | null;
  /** From `/clients`, counted BEFORE the band filter is applied so selecting one
   *  band does not zero every other chip and strand the rep in it. */
  bandCounts: Record<string, number>;
  options: SalesFilterOptions | null;
  onFiltersChange: (next: SalesFilterState) => void;
  /** `rows` on desktop; `dropdowns` where two chip rows do not fit; `stack` inside the phone sheet. */
  variant?: 'rows' | 'dropdowns' | 'stack';
  /** CSE-17 leader target lists; the Targets chip appears only when there is at least one. */
  targetLists?: SalesTargetList[];
}

/**
 * The labelled chip rows above the carousel (§6.2), matching Client View's
 * Tier / Priority rows.
 *
 * Two axes only: **Tier** ("how bad is it") and **Needs** ("what do I do about
 * it"). The narrower filters — why surfaced, engagement, flags — moved to the
 * carousel toolbar, where the rest of the list controls live.
 *
 * `OverflowRow` keeps each row on ONE line and moves the remainder into a "+N"
 * popover, so the rows cannot grow and push the carousel below the fold.
 */
export function SalesChipFilters({
  filters,
  summary,
  bandCounts,
  options,
  onFiltersChange,
  variant = 'rows',
  targetLists = [],
}: SalesChipFiltersProps) {
  const tierCounts = summary?.tier_counts;
  const tierCount = (t: string) =>
    tierCounts?.[t.toLowerCase() as keyof typeof tierCounts] ?? 0;
  const toggleTier = (t: string) => onFiltersChange({ ...filters, tier: toggleValue(filters.tier, t) });
  const toggleBand = (b: string) => onFiltersChange({ ...filters, band: toggleValue(filters.band, b) });
  const toggleTarget = (id: string) =>
    onFiltersChange({ ...filters, target_list: toggleValue(filters.target_list, id) });
  const targetOptions = targetLists.map((l) => ({
    value: l.id, label: l.reason, count: summary?.target_list_counts?.[l.id] ?? 0, color: TARGET_COLOR,
  }));
  const targetsDropdown = (dropdownVariant: 'button' | 'chip') => (
    <SalesFilterDropdown
      label="Targets"
      title="Clients on a leader's target list, by reason. Pick one or more."
      variant={dropdownVariant}
      count={summary?.targeted_count ?? 0}
      color={TARGET_COLOR}
      options={targetOptions}
      selected={filters.target_list}
      onToggle={toggleTarget}
      onClear={() => onFiltersChange({ ...filters, target_list: [] })}
    />
  );

  if (variant === 'dropdowns') {
    return (
      <div className="sv-chip-dropdowns">
        <SalesFilterDropdown
          label="Tier"
          title={TIER_HELP}
          options={TIER_ORDER.map((t) => ({
            value: t, label: t, count: tierCount(t), color: TIER_COLOR[t].pin,
          }))}
          selected={filters.tier}
          onToggle={toggleTier}
          onClear={() => onFiltersChange({ ...filters, tier: [] })}
        />
        {targetLists.length > 0 && targetsDropdown('button')}
        <SalesFilterDropdown
          label="Needs"
          options={BAND_ORDER.map((b) => ({
            value: b, label: BAND_LABEL[b], count: bandCounts[b] ?? 0, color: BAND_COLOR[b],
          }))}
          selected={filters.band}
          onToggle={toggleBand}
          onClear={() => onFiltersChange({ ...filters, band: [] })}
        />
      </div>
    );
  }

  const tierItems = TIER_ORDER.map((t) => {
    const active = filters.tier.includes(t);
    const count = tierCount(t);
    return {
      key: t,
      active,
      node: (
        <button
          type="button"
          className={`sv-chip ${active ? 'active' : ''}`}
          style={{ color: TIER_COLOR[t].pin }}
          aria-pressed={active}
          onClick={() => toggleTier(t)}
        >
          <span className="sv-chip-dot" />
          <span className="sv-chip-label">{t}</span>
          <span className="sv-chip-count">{count.toLocaleString()}</span>
        </button>
      ),
    };
  });

  // Driven by the band vocabulary rather than whatever this page happens to hold,
  // so a band the rep has work in never silently disappears from the row.
  const targetChips = targetOptions.map((o) => {
    const active = filters.target_list.includes(o.value);
    return (
      <button
        key={o.value}
        type="button"
        className={`sv-chip ${active ? 'active' : ''}`}
        style={{ color: TARGET_COLOR }}
        aria-pressed={active}
        onClick={() => toggleTarget(o.value)}
      >
        <span className="sv-chip-dot" />
        <span className="sv-chip-label">{o.label}</span>
        <span className="sv-chip-count">{o.count.toLocaleString()}</span>
      </button>
    );
  });
  // Inside the "+N" menu the dropdown renders flat, or its pop is clipped by the menu's scroll box.
  const tierRowItems = targetLists.length > 0
    ? [...tierItems, {
        key: 'targets', active: filters.target_list.length > 0, node: targetsDropdown('chip'),
        menuNode: <div className="sv-chip-stack-chips">{targetChips}</div>,
      }]
    : tierItems;

  const bandItems = BAND_ORDER.map((b) => {
    const active = filters.band.includes(b);
    const count = bandCounts[b] ?? 0;
    return {
      key: b,
      active,
      node: (
        <button
          type="button"
          className={`sv-chip ${active ? 'active' : ''}`}
          style={{ color: BAND_COLOR[b] }}
          aria-pressed={active}
          title={BAND_TIP[b]}
          onClick={() => toggleBand(b)}
        >
          <span className="sv-chip-dot" />
          <span className="sv-chip-label">{BAND_LABEL[b]}</span>
          <span className="sv-chip-count">{count.toLocaleString()}</span>
        </button>
      ),
    };
  });

  if (variant === 'stack') {
    return (
      <div className="sv-chip-stack">
        <div className="sv-chip-stack-group" role="group" aria-label="Tier" title={TIER_HELP}>
          <span className="sv-chip-stack-label">Tier</span>
          <div className="sv-chip-stack-chips">
            {tierItems.map((i) => <span key={i.key}>{i.node}</span>)}
          </div>
        </div>
        {targetLists.length > 0 && (
          <div className="sv-chip-stack-group" role="group" aria-label="Targets">
            <span className="sv-chip-stack-label">Targets</span>
            <div className="sv-chip-stack-chips">{targetChips}</div>
          </div>
        )}
        <div className="sv-chip-stack-group" role="group" aria-label="Needs">
          <span className="sv-chip-stack-label">Needs</span>
          <div className="sv-chip-stack-chips">
            {bandItems.map((i) => <span key={i.key}>{i.node}</span>)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sv-chip-rows">
      <OverflowRow className="sv-chip-row" label="Tier" title={TIER_HELP} items={tierRowItems} />
      <OverflowRow className="sv-chip-row" label="Needs" items={bandItems} />
      {options == null && <span className="sv-chip-row-note">Loading filters…</span>}
    </div>
  );
}

export default SalesChipFilters;
